import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Handshake, Building2, Mail, Phone, Clock, Briefcase,
  MessageSquare, Trash2, UserCheck, UserX, Search
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const getStatusColor = (status) => {
  const colors = {
    new: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    contacted: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    negotiating: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
    active: 'text-green-400 bg-green-400/10 border-green-400/20',
    declined: 'text-red-400 bg-red-400/10 border-red-400/20',
  };
  return colors[status] || 'text-slate-400 bg-slate-400/10 border-slate-400/20';
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminPartnersTab = ({ partners, stats, loading, onUpdateStatus, onDelete }) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const partnerStats = stats || { total: 0, new: 0, contacted: 0, negotiating: 0, active: 0, declined: 0 };

  const filtered = partners
    .filter(p => filter === 'all' || p.status === filter)
    .filter(p => {
      if (!search) return true;
      const q = search.toLowerCase();
      return p.business_name?.toLowerCase().includes(q) || p.contact_name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q);
    });

  return (
    <>
      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { key: 'total', label: 'Total', icon: Handshake, color: 'text-white' },
          { key: 'new', label: 'New', icon: Building2, color: 'text-emerald-400' },
          { key: 'contacted', label: 'Contacted', icon: Phone, color: 'text-blue-400' },
          { key: 'negotiating', label: 'Negotiating', icon: Briefcase, color: 'text-purple-400' },
          { key: 'active', label: 'Active', icon: UserCheck, color: 'text-green-400' },
          { key: 'declined', label: 'Declined', icon: UserX, color: 'text-red-400' },
        ].map(item => (
          <div key={item.key} className="bg-brand-surface rounded-xl border border-white/10 p-5" data-testid={`partner-stat-${item.key}`}>
            <div className="flex items-center gap-2 mb-2"><item.icon className={`w-4 h-4 ${item.color}`} /><span className="text-slate-400 text-xs">{item.label}</span></div>
            <p className={`text-2xl font-heading ${item.color}`}>{partnerStats[item.key] || 0}</p>
          </div>
        ))}
      </motion.div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search businesses..."
            className="w-full pl-10 pr-4 py-2.5 bg-brand-surface border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm"
            data-testid="partner-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'contacted', 'negotiating', 'active', 'declined'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filter === s ? 'bg-brand-red text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'
              }`}
              data-testid={`partner-filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Partner Cards */}
      {loading ? (
        <div className="text-center py-20 text-slate-500">Loading partner requests...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Handshake className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">No partner requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="bg-brand-surface rounded-xl border border-white/10 p-6 hover:border-white/20 transition-colors"
              data-testid={`partner-card-${p.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Info */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-white font-heading text-lg">{p.business_name}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(p.status)}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                    <span className="text-slate-400 flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5" /> {p.contact_name}
                    </span>
                    <a href={`mailto:${p.email}`} className="text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors">
                      <Mail className="w-3.5 h-3.5" /> {p.email}
                    </a>
                    <a href={`tel:${p.phone}`} className="text-slate-400 hover:text-white flex items-center gap-1.5 transition-colors">
                      <Phone className="w-3.5 h-3.5" /> {p.phone}
                    </a>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                    <span className="text-slate-500 flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5" /> {p.business_type}
                    </span>
                    {p.delivery_volume && (
                      <span className="text-slate-500 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> Volume: {p.delivery_volume}
                      </span>
                    )}
                    <span className="text-slate-600 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {formatDate(p.created_at)}
                    </span>
                  </div>
                  {p.message && (
                    <div className="flex items-start gap-1.5 mt-1">
                      <MessageSquare className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                      <p className="text-slate-500 text-sm">{p.message}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Select value={p.status} onValueChange={(val) => onUpdateStatus(p.id, val)}>
                    <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white" data-testid={`partner-status-select-${p.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-navy border-white/10">
                      {['new', 'contacted', 'negotiating', 'active', 'declined'].map(s => (
                        <SelectItem key={s} value={s} className="text-white hover:bg-white/10">{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" data-testid={`partner-delete-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-brand-navy border-white/10">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Partner Request</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          This will permanently delete the request from {p.business_name}. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(p.id)} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </>
  );
};

export default AdminPartnersTab;
